import type { LValue, Statement } from '../parser/ast.js';

/**
 * Ο μετρητής ενός ΓΙΑ είναι "κλειδωμένος" όσο εκτελείται το σώμα του —
 * μόνο ο ίδιος ο βρόχος τον προχωράει, ο μαθητής δεν επιτρέπεται να τον
 * αλλάξει χειροκίνητα (ανάθεση, ΔΙΑΒΑΣΕ, ΑΝΤΙΜΕΤΑΘΕΣΕ). Βλ.
 * `Interpreter.requireNotLockedForCounter` για το runtime αντίστοιχο. Αυτός
 * ο έλεγχος τρέχει ΠΡΙΝ την εκτέλεση (ίδια λογική με `findReusedLoopCounter`)
 * ώστε να πιάνει το λάθος ΑΚΟΜΑ ΚΙ αν βρίσκεται σε κλάδο που δεν θα
 * εκτελούνταν σε αυτό το συγκεκριμένο τρέξιμο. Ισχύει ΚΑΙ για ΓΛΩΣΣΑ ΚΑΙ
 * για Ψευδογλώσσα (γενικός κανόνας ορθότητας βρόχων, όχι ζήτημα δηλώσεων).
 *
 * ΜΟΝΟ για ΓΙΑ — όχι ΟΣΟ/ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ, όπου η χειροκίνητη ενημέρωση του
 * μετρητή είναι ο ΤΡΟΠΟΣ που δουλεύει ο βρόχος, όχι λάθος.
 *
 * Δεν ελέγχει ορίσματα ΚΑΛΕΣΕ (πέρασμα του μετρητή σαν όρισμα μόνο για
 * ανάγνωση είναι έγκυρο — "απόρριψη αντί για μάντεμα"· το runtime check
 * ΕΠΙΣΗΣ δεν το εμποδίζει, για τον ίδιο λόγο).
 */
export interface LockedCounterReassignment {
  readonly name: string;
  readonly line: number;
  readonly column: number;
}

function checkTarget(target: LValue, locked: ReadonlySet<string>): LockedCounterReassignment | null {
  if (target.kind !== 'Identifier') return null;
  return locked.has(target.name) ? { name: target.name, line: target.line, column: target.column } : null;
}

function checkStatement(stmt: Statement, locked: ReadonlySet<string>): LockedCounterReassignment | null {
  switch (stmt.kind) {
    case 'AssignmentStatement':
      return checkTarget(stmt.target, locked);
    case 'ReadStatement': {
      for (const target of stmt.targets) {
        const result = checkTarget(target, locked);
        if (result !== null) return result;
      }
      return null;
    }
    case 'SwapStatement': {
      const left = checkTarget(stmt.left, locked);
      if (left !== null) return left;
      return checkTarget(stmt.right, locked);
    }
    case 'ForStatement': {
      const nested = new Set(locked);
      nested.add(stmt.variable.name);
      return checkStatements(stmt.body, nested);
    }
    case 'IfStatement': {
      const thenResult = checkStatements(stmt.thenBranch, locked);
      if (thenResult !== null) return thenResult;
      for (const branch of stmt.elseIfBranches) {
        const branchResult = checkStatements(branch.body, locked);
        if (branchResult !== null) return branchResult;
      }
      if (stmt.elseBranch !== null) return checkStatements(stmt.elseBranch, locked);
      return null;
    }
    case 'WhileStatement':
      return checkStatements(stmt.body, locked);
    case 'RepeatUntilStatement':
      return checkStatements(stmt.body, locked);
    case 'SwitchStatement': {
      for (const switchCase of stmt.cases) {
        const result = checkStatements(switchCase.body, locked);
        if (result !== null) return result;
      }
      if (stmt.elseBody !== null) return checkStatements(stmt.elseBody, locked);
      return null;
    }
    case 'WriteStatement':
    case 'CallStatement':
      return null;
  }
}

function checkStatements(statements: readonly Statement[], locked: ReadonlySet<string>): LockedCounterReassignment | null {
  for (const stmt of statements) {
    const result = checkStatement(stmt, locked);
    if (result !== null) return result;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ χειροκίνητη αλλαγή κλειδωμένου μετρητή ΓΙΑ (με σειρά
 * εμφάνισης), ή `null` αν δεν υπάρχει καμία. Δουλεύει πάνω σε ΟΠΟΙΑΔΗΠΟΤΕ
 * λίστα εντολών — ο καλών πρέπει να ελέγξει το κύριο σώμα ΚΑΙ κάθε
 * υποπρόγραμμα ΞΕΧΩΡΙΣΤΑ (ίδιο μοτίβο με `findReusedLoopCounter`).
 */
export function findLockedForCounterReassignment(statements: readonly Statement[]): LockedCounterReassignment | null {
  return checkStatements(statements, new Set());
}
